import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { sendPush } from '@/lib/pushNotify';
import { toast } from 'sonner';
import { mockCommentStore } from '@/store/mockCommentStore';

function validateCommentContent(content: string): { valid: boolean; error?: string; trimmed: string } {
  const trimmed = content.trim();
  if (trimmed.length < 1) return { valid: false, error: "Comment cannot be empty", trimmed };
  if (trimmed.length > 1000) return { valid: false, error: "Comment must be less than 1000 characters", trimmed };
  return { valid: true, trimmed };
}

export interface Comment {
  id: string;
  user_id: string;
  review_id: string;
  content: string;
  created_at: string;
  updated_at?: string;
  parent_comment_id?: string | null;
  profile_name: string;
  profile_username: string;
  profile_picture?: string;
}

export const useComments = (reviewId: string) => {
  return useQuery({
    queryKey: ['comments', reviewId],
    queryFn: async (): Promise<Comment[]> => {
      const isMockReview = !reviewId || reviewId.length < 30 || reviewId.startsWith('review_');
      if (isMockReview) {
        return mockCommentStore.getComments(reviewId) as Comment[];
      }
      const { data: comments, error } = await supabase
        .from('comments')
        .select('*')
        .eq('review_id', reviewId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      if (!comments || comments.length === 0) return [];

      const userIds = Array.from(new Set(comments.map((c: any) => c.user_id)));
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, name, username, profile_picture')
        .in('id', userIds);
      const pmap = new Map((profiles || []).map((p: any) => [p.id, p]));

      return comments.map((c: any) => {
        const p: any = pmap.get(c.user_id);
        return {
          ...c,
          profile_name: p?.name || 'Anonymous',
          profile_username: p?.username || 'unknown',
          profile_picture: p?.profile_picture,
        } as Comment;
      });
    },
    enabled: !!reviewId,
    staleTime: 0,
  });
};

export const useCreateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reviewId, content, parentCommentId }: { reviewId: string; content: string; parentCommentId?: string | null }) => {
      const validation = validateCommentContent(content);
      if (!validation.valid) throw new Error(validation.error);
      const validatedContent = validation.trimmed;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Ensure profile exists
      const { data: existingProfile } = await supabase
        .from('profiles').select('id').eq('id', user.id).maybeSingle();
      if (!existingProfile) {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
            username: user.email?.split('@')[0] || `user_${user.id.slice(0, 8)}`,
          });
        if (profileError) throw new Error('Failed to create user profile. Please try again.');
      }

      // Rate limit
      const oneMinuteAgo = new Date(Date.now() - 60 * 1000).toISOString();
      const { data: recentComments } = await supabase
        .from('comments').select('id').eq('user_id', user.id).gte('created_at', oneMinuteAgo);
      if (recentComments && recentComments.length >= 5) {
        throw new Error('Rate limit exceeded. Please wait before posting another comment.');
      }

      // Normalize parent: if replying to a reply, attach to that reply's parent (1-level deep)
      let normalizedParent: string | null = parentCommentId || null;
      if (normalizedParent) {
        const { data: parent } = await supabase
          .from('comments').select('id, parent_comment_id, user_id').eq('id', normalizedParent).maybeSingle();
        if (parent?.parent_comment_id) normalizedParent = parent.parent_comment_id;
      }

      const isMockReview = !reviewId || reviewId.length < 30 || reviewId.startsWith('review_');
      if (isMockReview) {
        const { data: profile } = await supabase
          .from('profiles').select('name, username, profile_picture').eq('id', user.id).single();
        const mockComment: Comment = {
          id: `mock_${Date.now()}`,
          user_id: user.id,
          review_id: reviewId,
          content: validatedContent,
          created_at: new Date().toISOString(),
          parent_comment_id: normalizedParent,
          profile_name: profile?.name || 'You',
          profile_username: profile?.username || 'you',
          profile_picture: profile?.profile_picture,
        };
        mockCommentStore.addComment(reviewId, mockComment as any);
        return mockComment;
      }

      const { data, error } = await supabase
        .from('comments')
        .insert({
          review_id: reviewId,
          content: validatedContent,
          user_id: user.id,
          parent_comment_id: normalizedParent,
        } as any)
        .select('*')
        .single();
      if (error) throw error;
      console.log('[CommentPushDebug] comment inserted', {
        comment_id: (data as any).id,
        review_id: reviewId,
        parent_comment_id: normalizedParent,
      });

      // Activity + email
      try {
        const { data: reviewData } = await supabase
          .from('reviews').select('user_id, tour_name, artist_name').eq('id', reviewId).maybeSingle();

        // Resolve parent comment author for reply notifications
        let parentAuthorId: string | null = null;
        if (normalizedParent) {
          const { data: parent } = await supabase
            .from('comments').select('user_id').eq('id', normalizedParent).maybeSingle();
          parentAuthorId = parent?.user_id || null;
        }

        await supabase.from('activities').insert({
          user_id: user.id,
          type: normalizedParent ? 'comment_reply' : 'comment',
          target_id: reviewId,
          target_type: 'review',
          metadata: {
            review_id: reviewId,
            comment_id: (data as any).id,
            parent_comment_id: normalizedParent,
            target_user_id: normalizedParent ? parentAuthorId : reviewData?.user_id,
          },
        });

        const { data: actorProfile } = await supabase
          .from('profiles').select('name, username').eq('id', user.id).maybeSingle();

        const { sendPush } = await import('@/lib/pushNotify');
        const commentId = (data as any).id as string;
        const actorName = actorProfile?.name || actorProfile?.username || 'Someone';

        // -------- Parse @mentions FIRST so generic comment/reply paths can
        // skip recipients who will receive a mention-specific notification.
        const mentionedUsernames = Array.from(
          new Set(
            (validatedContent.match(/@([A-Za-z0-9_.]+)/g) || []).map((m) =>
              m.slice(1).toLowerCase(),
            ),
          ),
        );
        console.log('[mention] review-comment parsed', {
          review_id: reviewId,
          comment_id: commentId,
          count: mentionedUsernames.length,
          usernames: mentionedUsernames,
        });
        let mentionTargets: Array<{ id: string; username: string }> = [];
        if (mentionedUsernames.length) {
          const { data: mProfiles } = await supabase
            .from('profiles')
            .select('id, username')
            .in('username', mentionedUsernames);
          mentionTargets = (mProfiles || []).filter((p: any) => p.id !== user.id);
          console.log('[mention] review-comment matched profiles', {
            review_id: reviewId,
            resolved: mentionTargets.length,
            ids: mentionTargets.map((p) => p.id),
          });
        }
        const mentionIds = new Set(mentionTargets.map((p) => p.id));

        // Notify parent comment author on reply (skip if also mentioned)
        if (normalizedParent && parentAuthorId && parentAuthorId !== user.id) {
          if (mentionIds.has(parentAuthorId)) {
            console.log('[mention] suppressing comment_reply — recipient is mentioned', { parentAuthorId });
          } else {
            await supabase.from('notifications').insert({
              user_id: parentAuthorId,
              type: 'comment_reply',
              message: `${actorName} replied to your comment`,
              related_id: reviewId,
            });
            supabase.functions.invoke('send-notification-email', {
              body: {
                type: 'comment_reply',
                recipient_user_id: parentAuthorId,
                actor_user_id: user.id,
                dedupe_key: `reply:${commentId}`,
                metadata: {
                  actor_name: actorName,
                  actor_username: actorProfile?.username,
                  review_id: reviewId,
                  artist_name: reviewData?.artist_name,
                  tour_name: reviewData?.tour_name || reviewData?.artist_name,
                  comment_text: validatedContent,
                },
              },
            }).catch((err) => console.warn('reply email send failed', err));
            sendPush({
              type: 'comment_reply',
              recipient_user_id: parentAuthorId,
              actor_user_id: user.id,
              body: `${actorName} replied to your comment`,
              dedupe_key: `comment_reply:${commentId}`,
              data: { review_id: reviewId, comment_id: commentId },
            });
          }
        } else if (!normalizedParent && reviewData?.user_id) {
          const reviewOwnerId = reviewData.user_id;
          const subject = reviewData.tour_name || reviewData.artist_name || 'your review';
          const title = 'New comment on your review';
          const body = `${actorName} commented on your review of ${subject}`;
          const suppressForOwner =
            reviewOwnerId === user.id || mentionIds.has(reviewOwnerId);
          console.log('[CommentPushDebug] generic comment_on_review gate', {
            review_owner_id: reviewOwnerId,
            actor_user_id: user.id,
            owner_is_mentioned: mentionIds.has(reviewOwnerId),
            suppressed: suppressForOwner,
          });
          if (!suppressForOwner) {
            sendPush({
              type: 'comment_on_review',
              recipientUserId: reviewOwnerId,
              actorUserId: user.id,
              entityId: reviewId,
              entityType: 'review',
              title,
              body,
              dedupe_key: `comment_on_review:${commentId}`,
              data: { review_id: reviewId, comment_id: commentId },
            });
            await supabase.from('notifications').insert({
              user_id: reviewOwnerId,
              type: 'comment_on_review',
              message: `${actorName} commented on your review`,
              related_id: reviewId,
            });
            supabase.functions.invoke('send-notification-email', {
              body: {
                type: 'comment_on_review',
                recipient_user_id: reviewOwnerId,
                actor_user_id: user.id,
                dedupe_key: `comment:${commentId}`,
                metadata: {
                  actor_name: actorName,
                  actor_username: actorProfile?.username,
                  review_id: reviewId,
                  artist_name: reviewData.artist_name,
                  tour_name: reviewData.tour_name || reviewData.artist_name,
                  comment_text: validatedContent,
                },
              },
            }).catch((err) => console.warn('comment email send failed', err));
          }
        }

        // -------- Mention notifications: write to `activities` (single source
        // for in-app feed) + push + email. Dedupe by comment_id + recipient
        // so reprocessing the same comment never doubles up.
        if (mentionTargets.length) {
          try {
            const { data: existingMentions } = await supabase
              .from('activities')
              .select('id, metadata')
              .eq('type', 'mention')
              .filter('metadata->>comment_id', 'eq', commentId);
            const alreadyMentioned = new Set(
              (existingMentions || [])
                .map((row: any) => row?.metadata?.target_user_id)
                .filter(Boolean) as string[],
            );
            const freshTargets = mentionTargets.filter((p) => !alreadyMentioned.has(p.id));
            console.log('[mention] review-comment dedupe', {
              comment_id: commentId,
              requested: mentionTargets.length,
              already: alreadyMentioned.size,
              fresh: freshTargets.length,
            });

            if (freshTargets.length) {
              const { error: actErr } = await supabase.from('activities').insert(
                freshTargets.map((p) => ({
                  user_id: user.id,
                  type: 'mention',
                  target_id: p.id,
                  target_type: 'user',
                  metadata: {
                    source: 'review_comment',
                    review_id: reviewId,
                    comment_id: commentId,
                    target_user_id: p.id,
                    target_username: p.username,
                    artist_name: reviewData?.artist_name,
                    tour_name: reviewData?.tour_name,
                  },
                })),
              );
              console.log('[mention] activity insert', {
                count: freshTargets.length,
                error: actErr?.message ?? null,
              });
            }

            for (const p of freshTargets) {
              const dedupeKey = `mention:${commentId}:${p.id}`;
              sendPush({
                type: 'mention',
                recipient_user_id: p.id,
                actor_user_id: user.id,
                body: `${actorName} mentioned you in a comment`,
                dedupe_key: dedupeKey,
                data: {
                  review_id: reviewId,
                  comment_id: commentId,
                  source: 'review_comment',
                },
              });
              supabase.functions.invoke('send-notification-email', {
                body: {
                  type: 'mention',
                  recipient_user_id: p.id,
                  actor_user_id: user.id,
                  dedupe_key: dedupeKey,
                  metadata: {
                    actor_name: actorName,
                    actor_username: actorProfile?.username,
                    review_id: reviewId,
                    comment_id: commentId,
                    artist_name: reviewData?.artist_name,
                    tour_name: reviewData?.tour_name || reviewData?.artist_name,
                    comment_text: validatedContent,
                    source: 'review_comment',
                  },
                },
              }).catch((err) =>
                console.warn('[mention] email send failed', { recipient: p.id, err }),
              );
            }
          } catch (e) {
            console.warn('[mention] review-comment failed', e);
          }
        }


      } catch (err) {
        console.warn('Failed to track comment activity:', err);
      }

      const { data: profile } = await supabase
        .from('profiles').select('name, username, profile_picture').eq('id', user.id).single();

      return {
        ...(data as any),
        profile_name: profile?.name || 'Anonymous',
        profile_username: profile?.username || 'unknown',
        profile_picture: profile?.profile_picture,
      } as Comment;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['comments', data.review_id] });
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['friends-activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success(data.parent_comment_id ? 'Reply posted' : 'Comment added');
    },
    onError: (error) => {
      console.error('Error creating comment:', error);
      toast.error((error as any)?.message || 'Failed to add comment.');
    },
  });
};

export const useDeleteComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await supabase.from('comments').delete().eq('id', commentId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      toast.success('Comment deleted');
    },
    onError: () => toast.error('Failed to delete comment'),
  });
};
